import { useEffect, useState } from "react"
import type {
  PermitType,
  TemplateConfiguration,
} from "shared/api/templateService"
import {
  getAvailablePermitTypes,
  getTemplateConfiguration,
} from "shared/api/templateService"

interface UsePermitMasterDataParams {
  tenantId: string
  permitTypeCode?: string
  approvalStateId?: string | null
}

interface UsePermitMasterDataResult {
  permitTypes: PermitType[]
  isLoadingPermitTypes: boolean
  permitTypesError: string | null
  templateConfig: TemplateConfiguration | null
  isTemplateLoading: boolean
  templateError: string | null
}

export const usePermitMasterData = ({
  tenantId,
  permitTypeCode,
  approvalStateId,
}: UsePermitMasterDataParams): UsePermitMasterDataResult => {
  const [permitTypes, setPermitTypes] = useState<PermitType[]>([])
  const [isLoadingPermitTypes, setIsLoadingPermitTypes] = useState(false)
  const [permitTypesError, setPermitTypesError] = useState<string | null>(null)

  const [templateConfig, setTemplateConfig] = useState<TemplateConfiguration | null>(null)
  const [isTemplateLoading, setIsTemplateLoading] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    setIsLoadingPermitTypes(true)
    setPermitTypesError(null)

    getAvailablePermitTypes(tenantId)
      .then((types) => {
        if (!isMounted) return
        setPermitTypes(types ?? [])
      })
      .catch(() => {
        if (!isMounted) return
        setPermitTypes([])
        setPermitTypesError("Unable to load permit types")
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingPermitTypes(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [tenantId])

  useEffect(() => {
    let cancelled = false

    if (!permitTypeCode) {
      setTemplateConfig(null)
      setTemplateError(null)
      setIsTemplateLoading(false)
      return
    }

    setIsTemplateLoading(true)
    setTemplateError(null)

    // Always load canonical template configuration.
    // Stage-specific visibility/editability is handled on the frontend by section.approvalStageId.
    // This avoids backend fallback/generated fields (e.g. temporary comment fields from old test flow).
    getTemplateConfiguration(tenantId, permitTypeCode)
      .then((config) => {
        if (cancelled) return
        setTemplateConfig(config)
      })
      .catch(() => {
        if (cancelled) return
        setTemplateConfig(null)
        setTemplateError("Unable to load template configuration")
      })
      .finally(() => {
        if (!cancelled) {
          setIsTemplateLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, permitTypeCode, approvalStateId])

  return {
    permitTypes,
    isLoadingPermitTypes,
    permitTypesError,
    templateConfig,
    isTemplateLoading,
    templateError,
  }
}
